import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { showToast, toastStore } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { type IInboxCombine, InboxesApi, NAME_INBOX, TreeRootInboxes } from 'entities/Inbox'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IResponseInboxCombine } from 'entities/Inbox/api/types'
import { groupInboxTemplate } from 'entities/Inbox/templates/groupInboxTemplate'
import type { ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import type { IHandleAddTeamsGroupsCreateLocal } from 'pages/conversations/ui/ConversationMenu/store/types'

export class GroupsApiMenuStore {
  private _loadingCreateGroupTeam = false
  private _createFromGroupInbox: GroupInbox | null = null

  constructor(
    private _conversationMenuStore: ConversationMenuStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  setCreateFromGroupInbox = (item: GroupInbox) => {
    this._createFromGroupInbox = item
  }

  onAddTeamsGroupsCreateLocal = ({ inbox, groupInbox }: IHandleAddTeamsGroupsCreateLocal) => {
    const newInboxCreateId = NAME_INBOX.Create
    const newInboxCreateResponse = {
      ...groupInboxTemplate,
      id: newInboxCreateId,
      teams: [inbox],
      is_favorite: inbox.is_favorite,
    }

    const item = this._entities.inboxesStore.addItem(newInboxCreateResponse)
    if (item) this._conversationMenuStore.deleteItemTree(item.idTree)

    const newInboxGroup = this._entities.inboxesStore.getItem(newInboxCreateId)

    if (newInboxGroup) {
      this._conversationMenuStore.addItemTree({
        inbox: newInboxGroup,
        groupInbox: groupInbox,
      })
    }
  }

  onUpdateTeamsGroupsCreate = async (name: string, inbox: GroupInbox) => {
    const inboxCreateId = NAME_INBOX.Create

    try {
      runInAction(() => {
        this._loadingCreateGroupTeam = true
      })

      const params = {
        name: name,
        team_ids: inbox.teamsIds,
      }
      const item = this._entities.inboxesStore.getItem(inbox.teamsIds[0])

      if (this._createFromGroupInbox && item instanceof Inbox) {
        await this.onDeleteTeamsGroupsByIdRemoveTeamById(item, this._createFromGroupInbox)

        this._createFromGroupInbox = null
      }

      const { data } = await InboxesApi.updateTeamsGroupsCreate(params)

      const inboxGroupResponse: IResponseInboxCombine = {
        ...data,
        type: 'inbox_group',
      }

      this._entities.inboxesStore.addItem(inboxGroupResponse)

      const inboxGroup = this._entities.inboxesStore.getItem(inboxGroupResponse.id)

      this._conversationMenuStore.deleteItemTree(inboxCreateId)

      if (inboxGroup) {
        this._conversationMenuStore.addItemTree({
          inbox: inboxGroup,
        })

        if (inbox.isFavoriteType) {
          this._conversationMenuStore.updateFavorite({
            inbox: inboxGroup,
            status: !inboxGroup.isFavoriteType,
          })
        }
      }

      return inboxGroup
    } catch (e) {
      logger.error(e)

      this._conversationMenuStore.deleteItemTree(inboxCreateId)

      showToast({
        type: 'error',
        title: 'Folder name should contain min 3 characters',
      })
    } finally {
      runInAction(() => {
        this._loadingCreateGroupTeam = false
      })
    }
  }

  onDeleteTeamsGroupsByIdRemoveTeamById = async (inbox: Inbox, groupInbox?: GroupInbox) => {
    if (!groupInbox) return

    await this._entities.inboxesStore.groupInboxStore.removeTeamFromGroup(inbox, groupInbox)

    if (!groupInbox.teamsIds.length) {
      this.onDeleteTeamsGroupsById(groupInbox)
    }

    const nanoId = nanoid()
    let isUndoActionExecuted = false

    toastStore.add({
      id: nanoId,
      noCloseButton: true,
      type: 'info',
      title: `Inbox '${inbox.name}' removed from folder`,
      action: {
        text: 'Undo',
        onAction: async () => {
          if (isUndoActionExecuted) return

          isUndoActionExecuted = true

          runInAction(() => {
            toastStore.remove(nanoId)
          })

          if (groupInbox.teamsIds.length) {
            this.onCreateTeamsGroupsByIdAddTeams(inbox, groupInbox.id)
          } else {
            groupInbox.teamsIds = [inbox.id]

            const newGroupInbox = await this.onUpdateTeamsGroupsCreate(groupInbox.name, groupInbox)

            if (inbox.isFavoriteType && newGroupInbox) {
              groupInbox.syncOrigin({ ...groupInbox.origin, id: newGroupInbox.id })
              groupInbox.is_favorite = false
              this._conversationMenuStore.addItemTree({
                inbox,
              })
              this._conversationMenuStore.updateFavorite({
                inbox: newGroupInbox,
                status: true,
              })
            }

            groupInbox.handleUpdateTeamsIds([inbox.id])
          }
        },
      },
    })

    if (inbox.is_favorite !== groupInbox.is_favorite) {
      this._conversationMenuStore.deleteItemTree(`${groupInbox.idTree}_${inbox.id}`, 'child')

      const root_tree = inbox.is_favorite ? TreeRootInboxes.FAVORITE : TreeRootInboxes.SIMPLE

      this._conversationMenuStore.deleteItemTree(`${inbox.root_tree}_${inbox.id}`, 'delete')

      inbox.root_tree = root_tree
      this._conversationMenuStore.addItemTree({
        inbox,
      })
    } else {
      this._conversationMenuStore.deleteItemTree(`${groupInbox.idTree}_${inbox.id}`, 'child')
      this._onDeleteItemFromGroup(groupInbox)
    }
  }

  private _onDeleteItemFromGroup = (groupInbox: GroupInbox) => {
    this._conversationMenuStore.addItemTree({
      inbox: groupInbox,
    })
  }

  onDeleteTeamsGroupsById = async (inbox: GroupInbox) => {
    this._conversationMenuStore.deleteItemTree(inbox.idTree)
    await this._entities.inboxesStore.groupInboxStore.deleteGroup(inbox)
  }

  onCreateTeamsGroupsByIdAddTeams = async (inbox: IInboxCombine, groupId: number) => {
    const groupInbox = this._entities.inboxesStore.getItem(groupId)

    if (groupInbox && inbox.type === 'inbox_group') {
      await this._entities.inboxesStore.groupInboxStore.addTeamToGroup(inbox, groupId)
      this._conversationMenuStore.addItemTree({
        inbox: groupInbox,
      })
    }
  }

  onMoveTo = async (id: number, inbox: Inbox) => {
    const groupInbox = this._entities.inboxesStore.getItem(id)

    if (!groupInbox) return

    if (groupInbox.is_favorite !== inbox.is_favorite) {
      await this._conversationMenuStore.updateFavorite({
        inbox: inbox,
        status: groupInbox.is_favorite,
      })
    }

    await this.onCreateTeamsGroupsByIdAddTeams(inbox, id)
  }

  get loadingCreateGroupTeam() {
    return this._loadingCreateGroupTeam
  }
}
